export const BELOTE_SEATS = Object.freeze(["south", "west", "north", "east"]);
export const BELOTE_SUITS = Object.freeze(["clubs", "diamonds", "hearts", "spades"]);
export const BELOTE_RANKS = Object.freeze(["7", "8", "9", "J", "Q", "K", "10", "A"]);

const CARD_IDS = new Set(BELOTE_SUITS.flatMap((suit) => BELOTE_RANKS.map((rank) => `${suit}-${rank}`)));

export const REQUIRED_DEAL_FIELDS = Object.freeze([
  "players",
  "dealerSeat",
  "initialHand",
  "upcard",
  "bidding",
  "takerSeat",
  "trump",
  "plays",
  "tricks",
  "scores",
  "outcome",
]);

function confidenceValue(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(1, number));
}

function uniqueCards(cards = []) {
  return [...new Set(cards.filter((card) => CARD_IDS.has(card)))];
}

function playerValue(player = {}) {
  if (!BELOTE_SEATS.includes(player.seat)) return null;
  return {
    seat: player.seat,
    name: typeof player.name === "string" && player.name.trim() ? player.name.trim() : null,
    team: ["us", "them"].includes(player.team) ? player.team : null,
    confidence: confidenceValue(player.confidence),
  };
}

export function createDealState({ id, number = 1, startedAt = new Date().toISOString(), detectedFrom = "unknown" } = {}) {
  return {
    id,
    number,
    startedAt,
    endedAt: null,
    detectedFrom,
    players: [],
    dealerSeat: null,
    initialHand: [],
    fullHand: [],
    upcard: null,
    bidding: [],
    takerSeat: null,
    trump: null,
    plays: [],
    tricks: [],
    scores: { us: null, them: null },
    outcome: null,
    observations: 0,
    confidence: {},
  };
}

export function mergeVisibleObservation(deal, observation = {}) {
  if (!deal) return deal;
  const merged = { ...deal, confidence: { ...deal.confidence } };
  const confidence = observation.confidence || {};

  if (Array.isArray(observation.players)) {
    const bySeat = new Map(merged.players.map((player) => [player.seat, player]));
    for (const candidate of observation.players.map(playerValue).filter(Boolean)) {
      const previous = bySeat.get(candidate.seat);
      if (!previous || candidate.confidence >= previous.confidence) bySeat.set(candidate.seat, candidate);
    }
    merged.players = BELOTE_SEATS.map((seat) => bySeat.get(seat)).filter(Boolean);
  }

  for (const field of ["dealerSeat", "takerSeat"]) {
    if (BELOTE_SEATS.includes(observation[field]) && confidenceValue(confidence[field]) >= confidenceValue(merged.confidence[field])) {
      merged[field] = observation[field];
      merged.confidence[field] = confidenceValue(confidence[field]);
    }
  }
  if (BELOTE_SUITS.includes(observation.trump) && confidenceValue(confidence.trump) >= confidenceValue(merged.confidence.trump)) {
    merged.trump = observation.trump;
    merged.confidence.trump = confidenceValue(confidence.trump);
  }
  if (CARD_IDS.has(observation.upcard) && confidenceValue(confidence.upcard) >= confidenceValue(merged.confidence.upcard)) {
    merged.upcard = observation.upcard;
    merged.confidence.upcard = confidenceValue(confidence.upcard);
  }

  if (Array.isArray(observation.hand)) {
    const hand = uniqueCards(observation.hand);
    if (hand.length === 5 && !merged.initialHand.length) merged.initialHand = hand;
    if (hand.length >= merged.fullHand.length) merged.fullHand = hand;
    merged.confidence.hand = Math.max(confidenceValue(merged.confidence.hand), confidenceValue(confidence.hand));
  }

  if (observation.bid?.seat && BELOTE_SEATS.includes(observation.bid.seat)) {
    const action = observation.bid.action;
    const suit = observation.bid.suit;
    if (["pass", "take"].includes(action) && (action === "pass" || BELOTE_SUITS.includes(suit))) {
      const signature = `${observation.bid.round || 1}:${observation.bid.seat}:${action}:${suit || ""}`;
      if (!merged.bidding.some((bid) => bid.signature === signature)) {
        merged.bidding = [...merged.bidding, {
          signature,
          round: observation.bid.round || 1,
          seat: observation.bid.seat,
          action,
          suit: suit || null,
          at: observation.capturedAt || new Date().toISOString(),
          confidence: confidenceValue(confidence.bid),
        }];
      }
    }
  }

  if (observation.play?.seat && BELOTE_SEATS.includes(observation.play.seat) && CARD_IDS.has(observation.play.card)) {
    const trickNumber = Math.max(1, Number(observation.play.trickNumber) || 1);
    const signature = `${trickNumber}:${observation.play.seat}:${observation.play.card}`;
    if (!merged.plays.some((play) => play.signature === signature)) {
      merged.plays = [...merged.plays, {
        signature,
        trickNumber,
        order: Math.max(1, Number(observation.play.order) || 1),
        seat: observation.play.seat,
        card: observation.play.card,
        at: observation.capturedAt || new Date().toISOString(),
        confidence: confidenceValue(confidence.play),
      }];
    }
  }

  if (observation.trick?.number && observation.trick.winnerSeat && BELOTE_SEATS.includes(observation.trick.winnerSeat)) {
    const nextTrick = {
      number: Math.max(1, Number(observation.trick.number) || 1),
      winnerSeat: observation.trick.winnerSeat,
      points: Number.isFinite(Number(observation.trick.points)) ? Number(observation.trick.points) : null,
      confidence: confidenceValue(confidence.trick),
    };
    const previousIndex = merged.tricks.findIndex((trick) => trick.number === nextTrick.number);
    if (previousIndex < 0) merged.tricks = [...merged.tricks, nextTrick];
    else if (nextTrick.confidence >= merged.tricks[previousIndex].confidence) {
      merged.tricks = merged.tricks.map((trick, index) => index === previousIndex ? nextTrick : trick);
    }
  }

  if (observation.scores) {
    merged.scores = {
      us: Number.isFinite(Number(observation.scores.us)) ? Number(observation.scores.us) : merged.scores.us,
      them: Number.isFinite(Number(observation.scores.them)) ? Number(observation.scores.them) : merged.scores.them,
    };
    merged.confidence.scores = Math.max(confidenceValue(merged.confidence.scores), confidenceValue(confidence.scores));
  }
  if (["win", "loss", "draw"].includes(observation.outcome)) merged.outcome = observation.outcome;
  merged.observations += 1;
  return merged;
}

export function dealCompleteness(deal) {
  if (!deal) return { complete: false, ratio: 0, missing: [...REQUIRED_DEAL_FIELDS] };
  const present = {
    players: deal.players.length === 4 && deal.players.every((player) => player.name),
    dealerSeat: Boolean(deal.dealerSeat),
    initialHand: deal.initialHand.length === 5,
    upcard: CARD_IDS.has(deal.upcard),
    bidding: deal.bidding.length > 0,
    takerSeat: Boolean(deal.takerSeat),
    trump: BELOTE_SUITS.includes(deal.trump),
    plays: deal.plays.length === 32,
    tricks: deal.tricks.length === 8,
    scores: Number.isFinite(deal.scores.us) && Number.isFinite(deal.scores.them),
    outcome: ["win", "loss", "draw"].includes(deal.outcome),
  };
  const missing = REQUIRED_DEAL_FIELDS.filter((field) => !present[field]);
  return {
    complete: missing.length === 0,
    ratio: (REQUIRED_DEAL_FIELDS.length - missing.length) / REQUIRED_DEAL_FIELDS.length,
    missing,
  };
}

export function finalizeDealState(deal, { endedAt = new Date().toISOString(), reason = "deal-ended" } = {}) {
  if (!deal) return null;
  const finalized = { ...deal, endedAt, reason };
  finalized.completeness = dealCompleteness(finalized);
  return finalized;
}

export function shouldRotateDeal({ activeDeal, previousPhase, currentPhase, stablePhaseFrames = 0 } = {}) {
  return Boolean(activeDeal?.plays?.length || activeDeal?.hadPlay)
    && previousPhase !== "bidding"
    && currentPhase === "bidding"
    && stablePhaseFrames >= 2;
}

